import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Crown, Shield, Edit3, Eye, FileText, Trash2,
  Loader2, Mail, MoreVertical, AlertTriangle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_INFO = {
  owner: { label: 'Owner', icon: Crown, color: 'bg-amber-100 text-amber-700', description: 'Full access, cannot be removed' },
  admin: { label: 'Admin', icon: Shield, color: 'bg-purple-100 text-purple-700', description: 'Manage forms, submissions, team' },
  editor: { label: 'Editor', icon: Edit3, color: 'bg-blue-100 text-blue-700', description: 'Create & edit forms' },
  viewer: { label: 'Viewer', icon: Eye, color: 'bg-slate-100 text-slate-700', description: 'View forms & submissions only' },
  submission_manager: { label: 'Submission Manager', icon: FileText, color: 'bg-green-100 text-green-700', description: 'Access submissions only' },
};

export const TeamPage = () => {
  const { token, user } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchTeam = useCallback(async () => {
    // Get token from localStorage as fallback
    const authToken = token || localStorage.getItem('token');
    
    if (!authToken) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/team`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeam(data.team !== undefined ? data : data);
      } else if (response.status === 401) {
        console.error('Team fetch: Unauthorized - token may be invalid');
      }
    } catch (error) {
      console.error('Failed to load team:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchTeam();
    } else {
      setLoading(false);
    }
  }, [token, fetchTeam]);

  const handleInvite = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    // Get token from localStorage as fallback
    const authToken = token || localStorage.getItem('token');
    
    if (!authToken) {
      toast.error('You must be logged in to invite members');
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Invitation sent to ${inviteEmail}`);
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteRole('viewer');
        fetchTeam();
      } else {
        toast.error(data.detail || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Invite error:', error);
      toast.error('Failed to send invitation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/team/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Team member removed');
        fetchTeam();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to remove member');
      }
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/team/members/${memberId}/role?new_role=${newRole}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Role updated');
        fetchTeam();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  // No team yet - show invite member directly (team will be auto-created)
  if (!team || team.team === null) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardContent className="pt-12 pb-12 text-center">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Invite Team Members</h2>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Invite people to collaborate on your forms. They'll receive an email to set up their account and access your dashboard.
              </p>
              <Button onClick={() => setShowInviteModal(true)} size="lg" data-testid="invite-member-btn">
                <UserPlus className="w-5 h-5 mr-2" />
                Invite Member
              </Button>
              
              <div className="mt-8 pt-8 border-t">
                <h3 className="font-semibold text-slate-900 mb-4">Available Roles</h3>
                <div className="grid md:grid-cols-2 gap-3 text-left">
                  {Object.entries(ROLE_INFO).filter(([key]) => key !== 'owner').map(([key, info]) => {
                    const Icon = info.icon;
                    return (
                      <div key={key} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className={`p-2 rounded-lg ${info.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{info.label}</p>
                          <p className="text-sm text-slate-500">{info.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invite Modal */}
          <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite Team Member</DialogTitle>
                <DialogDescription>
                  They'll receive an email with a link to set up their account
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@company.com"
                    data-testid="invite-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin - Manage forms, submissions, team</SelectItem>
                      <SelectItem value="editor">Editor - Create & edit forms</SelectItem>
                      <SelectItem value="viewer">Viewer - View forms & submissions only</SelectItem>
                      <SelectItem value="submission_manager">Submission Manager - Access submissions only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleInvite} disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Send Invitation
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </DashboardLayout>
    );
  }

  const isOwner = team.is_owner;
  const canManage = isOwner || team.my_role === 'admin';

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-6 h-6" />
              {team.name || 'My Team'}
            </h1>
            <p className="text-slate-600 mt-1">
              {isOwner ? 'You are the team owner' : `Your role: ${ROLE_INFO[team.my_role]?.label}`}
            </p>
          </div>
          
          {canManage && (
            <Button onClick={() => setShowInviteModal(true)} data-testid="invite-member-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Member
            </Button>
          )}
        </div>

        {/* Plan Limit Info */}
        <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-indigo-900">Team Members</p>
                <p className="text-sm text-indigo-700">
                  {team.members?.length || 1} member{(team.members?.length || 1) !== 1 ? 's' : ''}
                  {user?.plan === 'free' && ' • Free plan: 1 team member'}
                  {user?.plan === 'growth' && ' • Growth plan: Up to 5 team members'}
                  {user?.plan === 'pro' && ' • Pro plan: Unlimited team members'}
                </p>
              </div>
              {user?.plan === 'free' && (
                <Button variant="outline" size="sm" className="border-indigo-300 text-indigo-700">
                  Upgrade for more
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Team Members */}
        <Card>
          <CardHeader>
            <CardTitle>Team Members</CardTitle>
            <CardDescription>Manage your team members and their access levels</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {team.members?.map((member) => {
                const roleInfo = ROLE_INFO[member.role] || ROLE_INFO.viewer;
                const Icon = roleInfo.icon;
                const isCurrentUser = member.user_id === user?.id;
                
                return (
                  <div 
                    key={member.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    data-testid={`team-member-${member.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-indigo-600 font-semibold">
                          {member.full_name?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {member.full_name || member.email}
                          {isCurrentUser && <span className="text-slate-500 ml-2">(You)</span>}
                        </p>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {member.email}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Badge className={roleInfo.color}>
                        <Icon className="w-3 h-3 mr-1" />
                        {roleInfo.label}
                      </Badge>
                      
                      {member.member_status === 'pending' && (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          Pending
                        </Badge>
                      )}
                      
                      {canManage && member.role !== 'owner' && !isCurrentUser && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleChangeRole(member.id, 'admin')}>
                              <Shield className="w-4 h-4 mr-2" />
                              Make Admin
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleChangeRole(member.id, 'editor')}>
                              <Edit3 className="w-4 h-4 mr-2" />
                              Make Editor
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleChangeRole(member.id, 'viewer')}>
                              <Eye className="w-4 h-4 mr-2" />
                              Make Viewer
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleChangeRole(member.id, 'submission_manager')}>
                              <FileText className="w-4 h-4 mr-2" />
                              Make Submission Manager
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleRemoveMember(member.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Invite Modal */}
        <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
              <DialogDescription>
                Send an invitation to collaborate on your forms
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  data-testid="invite-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin - Manage forms, submissions, team</SelectItem>
                    <SelectItem value="editor">Editor - Create & edit forms</SelectItem>
                    <SelectItem value="viewer">Viewer - View forms & submissions only</SelectItem>
                    <SelectItem value="submission_manager">Submission Manager - Access submissions only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInviteModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleInvite} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Send Invitation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TeamPage;
